import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import Loader from '../components/loader/Loader'
import Toast from '../components/toast/Toaster'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastState {
  message: string
  type: ToastType
  show: boolean
}

interface UIContextType {
  showLoader: () => void
  hideLoader: () => void
  showToast: (message: string, type?: ToastType, duration?: number) => void
}

const UIActionsContext = createContext<UIContextType | undefined>(undefined)

interface UIProviderProps {
  children: ReactNode
}

const defaultToastState: ToastState = {
  message: '',
  type: 'success',
  show: false,
}

export function UIProvider({ children }: UIProviderProps) {
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<ToastState>(defaultToastState)
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showLoader = useCallback(() => {
    setLoading(true)
  }, [])

  const hideLoader = useCallback(() => {
    setLoading(false)
  }, [])

  const closeToast = useCallback(() => {
    setToast(defaultToastState)
  }, [])

  const showToast = useCallback(
    (message: string, type: ToastType = 'success', duration = 4000) => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current)
      }

      setToast({ message, type, show: true })
      toastTimeoutRef.current = setTimeout(closeToast, duration)
    },
    [closeToast]
  )

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current)
      }
    }
  }, [])

  const value = useMemo(
    () => ({
      showLoader,
      hideLoader,
      showToast,
    }),
    [hideLoader, showLoader, showToast]
  )

  return (
    <UIActionsContext.Provider value={value}>
      {children}
      {loading && <Loader />}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}
    </UIActionsContext.Provider>
  )
}

export const useUIContext = (): UIContextType => {
  const context = useContext(UIActionsContext)

  if (!context) {
    throw new Error('useUIContext must be used within a UIProvider')
  }

  return context
}
